import { useEffect, useState } from "react";
import type { Money } from "@amber/contracts";
import { SegmentedFilter, Panel } from "@amber/ui";
import type { BudgetsResponse, SpendRow } from "../../contracts/index";
import { num, usd } from "./money";
import { Name, Table, Td, Th } from "./parts";

function ShareBar({ share }: { share: number }) {
  return (
    <div className="grid grid-cols-[minmax(70px,1fr)_44px] items-center gap-2.5">
      <div
        role="img"
        aria-label={`${share.toFixed(1)}% of this month's spend`}
        className="relative h-2 min-w-[70px] rounded-full bg-surface-3"
      >
        <span
          className="absolute inset-y-0 left-0 rounded-full bg-link"
          style={{ width: `${String(Math.min(100, share))}%` }}
        />
      </div>
      <span className="text-right font-mono text-md whitespace-nowrap">
        {share > 0 && share < 0.5 ? "<1" : Math.round(share)}%
      </span>
    </div>
  );
}

const BREAKDOWNS = [
  { value: "provider", label: "Provider" },
  { value: "stage", label: "Stage" },
  { value: "person", label: "Person" },
  { value: "platform", label: "Platform" },
] as const;

type Breakdown = (typeof BREAKDOWNS)[number]["value"];

export function SpendPanel({ data }: { data: BudgetsResponse }) {
  const [by, setBy] = useState<Breakdown>("provider");
  useEffect(() => {
    if (window.location.hash === "#spend") document.getElementById("spend")?.scrollIntoView({ block: "start" });
  }, []);
  const breakdown = data.breakdowns[by];
  const label = BREAKDOWNS.find((b) => b.value === by)?.label ?? "Provider";
  const money = (m: Money) => (num(m) === 0 ? <span className="text-muted">—</span> : usd(m));
  const total = num(data.total_spend.incurred) + num(data.total_spend.committed);
  const un = breakdown.unallocated;
  const row = (r: SpendRow, muted = false) => (
    <tr key={r.label}>
      <Td>
        <Name detail={r.detail}>{muted ? <span className="text-muted">{r.label}</span> : r.label}</Name>
      </Td>
      <Td right className="tabular-nums text-fg">
        {money(r.incurred)}
      </Td>
      <Td right className="tabular-nums text-fg">
        {money(r.committed)}
      </Td>
      <Td>
        {num(r.incurred) + num(r.committed) > 0 ? <ShareBar share={r.share} /> : <span className="text-muted">—</span>}
      </Td>
    </tr>
  );
  return (
    <Panel
      id="spend"
      title="Where the money goes this month"
      note={`${data.period.month}, day ${String(data.period.day)} of ${String(data.period.days)} · incurred and committed`}
      actions={
        <>
          <span className="text-md text-muted">Break down by</span>
          <SegmentedFilter label="Break down by" value={by} options={BREAKDOWNS} onChange={setBy} />
        </>
      }
    >
      <Table
        label={`Spend by ${label.toLowerCase()}`}
        minWidth={560}
        head={
          <>
            <Th className="w-[40%]">{label}</Th>
            <Th right>Incurred</Th>
            <Th right>Committed</Th>
            <Th className="min-w-[150px]">Share</Th>
          </>
        }
      >
        {breakdown.rows.length === 0 ? (
          <tr>
            <Td colSpan={4}>No spend recorded this month yet.</Td>
          </tr>
        ) : null}
        {breakdown.rows.map((r) => row(r))}
        {row(un, true)}
        <tr className="[&>td]:bg-surface-2">
          <Td>
            <span className="font-semibold text-fg">Total</span>
          </Td>
          <Td right className="font-semibold tabular-nums text-fg">
            {usd(data.total_spend.incurred)}
          </Td>
          <Td right className="font-semibold tabular-nums text-fg">
            {usd(data.total_spend.committed)}
          </Td>
          <Td className="font-semibold tabular-nums text-fg">{usd(total)} · 100%</Td>
        </tr>
      </Table>
      <p className="m-0 mt-2.5 max-w-[780px] text-ui text-muted">
        {by === "person"
          ? "A cost shared by several people is split evenly between them, so it is counted once. "
          : null}
        Costs that can&apos;t be tied to one {label.toLowerCase()} stay in Unallocated rather than being spread out. The
        total matches the allowances above.
      </p>
    </Panel>
  );
}
